import { useState } from 'react';
import { Opt } from 'record-it-utils/misc/Opt/Opt';
import { StringUtils } from 'record-it-utils/utils/StringUtils/StringUtils';
import { useEventCallback } from '../useEventCallback';

export function useAsyncCallback<
  TArgs extends Array<any>,
  TReturnType extends Promise<any>,
  TName extends Readonly<string>
>({
  callback,
  name,
  onSuccess,
  onError
}: {
  callback: (...args: TArgs) => TReturnType;
  name: TName;
  onSuccess?: (value: Awaited<TReturnType>) => void;
  onError?: (error: unknown) => void;
}): UseAsyncCallbackOnceResult<TArgs, TReturnType, TName> {
  const [isRunning, setIsRunning] = useState(false);
  const [lastResult, setLastResult] = useState<LastResult<TReturnType>>(
    Opt.none()
  );

  const start = useEventCallback<(...args: TArgs) => void>((...args) => {
    if (isRunning) {
      throw new AsyncCallbackAlreadyRunningError();
    }

    setIsRunning(true);
    void callback(...args)
      .then((value) => {
        setLastResult(
          Opt.some({
            success: true,
            value
          })
        );
        setIsRunning(false);
        onSuccess?.(value);
      })
      .catch((error) => {
        setLastResult(
          Opt.some({
            success: false,
            error
          })
        );
        setIsRunning(false);
        onError?.(error);
      });
  });

  return {
    [name === '' ? 'start' : `start${StringUtils.upperCaseFirstLetter(name)}`]:
      start,
    [name === ''
      ? 'isRunning'
      : `${StringUtils.lowerCaseFirstLetter(name)}IsRunning`]: isRunning,
    [name === ''
      ? 'lastResult'
      : `last${StringUtils.upperCaseFirstLetter(name)}Result`]: lastResult
  } as UseAsyncCallbackOnceResult<TArgs, TReturnType, TName>;
}

export type UseAsyncCallbackOnceResult<
  TArgs extends Array<any>,
  TReturnType extends Promise<any>,
  TName extends Readonly<string>
> = Record<
  TName extends '' ? 'start' : `start${Capitalize<TName>}`,
  (...args: TArgs) => void
> &
  Record<TName extends '' ? 'isRunning' : `${TName}IsRunning`, boolean> &
  Record<
    TName extends '' ? 'lastResult' : `last${Capitalize<TName>}Result`,
    LastResult<TReturnType>
  >;

export type LastResult<TReturnType extends Promise<any>> = Opt<
  | {
      success: true;
      value: Awaited<TReturnType>;
    }
  | {
      success: false;
      error: unknown;
    }
>;

export class AsyncCallbackAlreadyRunningError extends Error {
  constructor() {
    super("The async callback is already running and can't be called again");
  }
}
